import React, {
  useRef, useMemo, useEffect, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { useTranslation } from '@common/hooks/translation';
import { getBetBuilderURL, handleBetBuilderUpdateData } from './helpers';

import './style.scss';

const BetBuilder = ({
  title,
  language,
  skinConfig,
  closePopup,
  oddsFormat,
  sportcastId,
  updateHandler,
  isBetslipVisible,
  resetInactiveTimer,
}) => {
  const iframeRef = useRef();
  const t = useTranslation();
  const dispatch = useDispatch()

  const url = useMemo(() => getBetBuilderURL(sportcastId, oddsFormat, language, skinConfig), [sportcastId, oddsFormat, language, skinConfig]);

  const onPostMessage = useCallback((event) => {
    if (event.source === iframeRef.current?.contentWindow) {
      const parsedData = JSON.parse(event.data);
      const data = handleBetBuilderUpdateData(parsedData, skinConfig, oddsFormat);
      updateHandler(parsedData.command, data);
    }
  }, [iframeRef, skinConfig, oddsFormat, updateHandler]);

  useEffect(() => {
    iframeRef.current && window.addEventListener('message', onPostMessage);
    return () => window.removeEventListener('message', onPostMessage);
  }, [iframeRef, onPostMessage]);

  useEffect(() => {
    if (skinConfig.unfocusForInactivity) {
      const intervalId = setInterval(() => {
        const iframe = iframeRef.current;
        const { activeElement } = document;

        if (activeElement === iframe) {
          activeElement.blur();
          resetInactiveTimer()
        }
      }, 2000);

      return () => clearInterval(intervalId);
    }
  }, [dispatch]);

  return (
    <div className="bet-builder-container">
      <div className="bet-builder-content">
        <div className="bet-builder-header">
          <div className="bet-builder-header-txt">
            <span>
              {t('BetBuilder')}
            </span>
            <span>
              {title}
            </span>
          </div>
          <div className="bet-builder-header-btn">
            <button className="global-icon icon-Cancel" onClick={closePopup} />
          </div>
        </div>
        <iframe
          src={url}
          ref={iframeRef}
          className={classnames('external-bet-builder', { 'betslip-visible': isBetslipVisible })}
        />
      </div>
    </div>
  );
};

BetBuilder.propTypes = {
  language: PropTypes.string,
  updateHandler: PropTypes.func,
  oddsFormat: PropTypes.string.isRequired,
  sportcastId: PropTypes.number.isRequired,
  skinConfig: PropTypes.shape({
    url: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    licenseKey: PropTypes.string.isRequired,
    oddsMap: PropTypes.object.isRequired,
    languageMap: PropTypes.object.isRequired,
  }).isRequired,
};

export default BetBuilder;
